import React from "react";
import { Helmet } from "react-helmet";
import {
    Container,
    Typography,
    Paper,
    Button,
    Grid,
    Box,
    Divider
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import QuizIcon from "@mui/icons-material/Quiz";
import GroupIcon from '@mui/icons-material/Group';
import { Reveal } from "../util/Reveal";

export function StreamerPage() {
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Helmet>
                <title>Skrach.it for Streamers – Boost Engagement with Live Quizzes</title>
                <meta name="description" content="Turn your stream into a live game show and let your community be the contestants with Skrach.it – a free, interactive quiz extension that works on any streaming platform." />
                <meta property="og:title" content="Skrach.it for Streamers – Interactive Twitch Extension" />
                <meta property="og:description" content="Let your viewers scratch, guess, and compete in real time. Free Twitch extension to make your stream unforgettable." />
                <meta property="og:url" content="https://skrach.it/streamers" />
            </Helmet>

            <Reveal animation="zoom" delay={500}>
                <Paper elevation={6} sx={{ p: 6, textAlign: "center", mb: 8, borderRadius: 4, background: "radial-gradient(circle at top left, #2e2e2e, #1c1f2a)", boxShadow: "0 0 30px rgba(180,160,235,0.4)", border: "2px solid #b4a0eb", color: "#fff" }}>
                    <GroupIcon sx={{ fontSize: 75, mb: 5, color: "#fff" }} />
                    <Typography variant="h3" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif", color: "#ffe066" }}>
                        Skrach.it for Streamers – Boost Engagement with Live Quizzes
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 3, color: "#fff", fontFamily: "Orbitron, sans-serif" }}>
                        Turn your stream into a game show – and let your community be the contestants.
                    </Typography>
                    <Typography sx={{ mt: 2, fontSize: "1.1rem", color: "#ccc" }}>
                        Skrach.it transforms any livestream into an interactive scratch-and-guess quiz that your viewers can play in real time. Reveal clues, let chat guess, and keep the energy high — across any platform.
                    </Typography>
                </Paper>
            </Reveal>

            <Reveal animation="fade" delay={700}>
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif" }}>
                        What is Skrach.it?
                    </Typography>
                    <Typography sx={{ mb: 2, fontSize: "1.2rem", color: "#000" }}>
                        Picture this: you slowly reveal an image or trivia clue on stream. Your viewers are going wild in chat, trying to figure it out. Some genuinely try to help. Others might troll you by voting for the wrong answer just to throw you off — a little friendly bait for the streamer 😈.
                    </Typography>
                    <Typography sx={{ mb: 2, fontSize: "1.2rem", color: "#000" }}>
                        Will you trust the crowd or go with your gut? This unpredictable back-and-forth turns passive viewers into active participants. And most importantly — it’s fun.
                    </Typography>
                    <Typography sx={{ mb: 2, fontSize: "1.2rem", color: "#000" }}>
                        Interactive elements like live quizzes have been shown to significantly boost retention and engagement. Because let’s face it: today’s audiences don’t want to just watch — they want in.
                    </Typography>
                </Box>
            </Reveal>

            <Divider sx={{ my: 7 }} />

            <Reveal animation="slide-up" delay={800}>
                <Typography variant="h3" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif", mb: 4 }}>
                    Why Use Skrach.it On Your Stream?
                </Typography>

                <Grid container spacing={4}>
                    {[
                        "💬 Boost viewer engagement and chat activity: No more silent lurkers. When viewers play along, they stay longer and chat more.",
                        "🎉 Perfect for trivia, warm-ups, or party games: Icebreakers, mid-stream fun, or full quiz nights – Skrach.it fits any moment.",
                        "🎛️ You’re in control – customize everything: Select topics, difficulty, pace, and timing. Tailor the quiz to your vibe.",
                        "📱 No downloads needed – viewers play on any device: Phone, tablet, smart TV, desktop – it just works via browser."
                    ].map((text, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Paper elevation={2} sx={{ p: 3, background: "#1c1f2a", borderLeft: "6px solid #29b6f6", borderRadius: 2, fontSize: "1rem", fontWeight: 500 }}>
                                {text}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Reveal>

            <Divider sx={{ my: 7 }} />

            <Reveal animation="slide-up" delay={650}>
                <Typography variant="h3" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif", mb: 4 }}>
                    How It Works
                </Typography>
                <Grid container spacing={4}>
                    {[
                        {
                            icon: <AutoAwesomeMotionIcon sx={{ fontSize: 40, color: "#ff7043" }} />,
                            title: "Create Your Quiz Room",
                            text: "Install the Twitch extension, or create a browser-based room on skrach.it. It takes seconds."
                        },
                        {
                            icon: <QuizIcon sx={{ fontSize: 40, color: "#29b6f6" }} />,
                            title: "Invite Your Viewers",
                            text: "Drop the room link in chat or show a QR code on screen. One tap and they’re in — no login required."
                        },
                        {
                            icon: <GroupIcon sx={{ fontSize: 40, color: "#4caf50" }} />,
                            title: "Scratch and Play Live",
                            text: "Reveal clues in real time and let your community compete, joke, bait, and guess — all on stream."
                        },
                        {
                            icon: <EmojiEventsIcon sx={{ fontSize: 40, color: "#ffe066" }} />,
                            title: "Reveal Winners",
                            text: "See who nailed it, celebrate the fastest minds, and keep the chat hype rolling for the next round."
                        }
                    ].map((step, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, backgroundColor: "#1c1f2a", color: "#fff" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                    {step.icon}
                                    <Typography variant="h6" color="#ffe066">Step {i + 1}</Typography>
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                    {step.title}
                                </Typography>
                                <Typography sx={{ fontSize: "0.95rem", color: "#ccc" }}>{step.text}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Reveal>

            <Divider sx={{ my: 7 }} />

            <Reveal animation="fade" delay={500}>
                <Typography variant="h3" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif", mb: 4 }}>
                    Works Everywhere You Stream
                </Typography>
                <Typography sx={{ fontSize: "1.05rem", color: "#000", mb: 2 }}>
                    🟣 <strong>On Twitch:</strong> Use our official Twitch extension. Everything appears right on your stream — smooth and integrated.
                </Typography>
                <Typography sx={{ fontSize: "1.05rem", color: "#000", mb: 2 }}>
                    🌐 <strong>On Other Platforms:</strong> Stream on YouTube, Facebook, Kick, or anywhere else? Just open a browser-based room and share the link or QR code. Viewers join with zero friction.
                </Typography>
                <Typography sx={{ fontSize: "1.05rem", color: "#000", mb: 2 }}>
                    Use Skrach.it overlay in OBS or other software to show quiz results or questions live. It’s 100% platform-agnostic.
                </Typography>
            </Reveal>

            <Divider sx={{ my: 7 }} />

            <Reveal animation="zoom" delay={1000}>
                <Box textAlign="center" sx={{ mt: 8, p: 4, background: "linear-gradient(to right, #3e8e93, #2c5364)", borderRadius: 3, boxShadow: "0 0 15px rgba(180,160,235,0.5)" }}>
                    <Typography variant="h4" gutterBottom sx={{ fontFamily: "Orbitron, sans-serif", mb: 3, color: "#fff" }}>
                        Ready to Engage Your Audience Like Never Before?
                    </Typography>
                    <Typography sx={{ mb: 3, color: "#eee", fontSize: "1.2rem" }}>
                        Whether it’s trivia night or a fast round between games, Skrach.it keeps chat fired up and viewers locked in.
                    </Typography>
                    <Button variant="contained" size="large" href="https://skrach.it/login" sx={{ fontSize: "1.2rem", fontWeight: "bold", backgroundColor: "#7a52e0", '&:hover': { backgroundColor: "#b4a0eb" } }}>
                        Sign In & Create Your Room
                    </Button>
                </Box>
            </Reveal>
        </Container>
    );
}
